import { translate } from "@jsverse/transloco";
import { Component, effect, inject, input, signal } from '@angular/core';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { SkinningService } from '@core/skin';
import { PlayerService } from 'app/features/player/data-access/player.service';
import { CommonModule, DatePipe } from '@angular/common';
import { clpp } from '@castlabs/prestoplay';

@Component({
  selector: 'player-controls-settings',
  imports: [
    CommonModule,
    RoundedIconButton,
    DatePipe,
  ],
  templateUrl: './player-controls-settings.html',
  styleUrl: './player-controls-settings.scss'
})
export class PlayerControlsSettingsComponent {
  public skinning = inject(SkinningService);
  private playerService = inject(PlayerService);

  public audioTracks = signal<Array<any>>([]);
  public selectedAudioTrack = signal<any>(null);
  public subtitleTracks = signal<Array<any>>([]);
  public selectedSubtitleTrack = signal<any>(null);
  /** Speeds outside [minSpeed, maxSpeed] stay listed but greyed out (live TV, see player-controls). */
  public minSpeed = input<number>(0);
  public maxSpeed = input<number>(Infinity);
  public selectedSpeed = signal<any>(null);

  public menus = signal<Array<any>>([]);
  public availableSpeeds: number[] = [0.25, 0.5, 1, 1.25, 1.5, 2];

  private state = this.playerService.getState();
  private item = this.playerService.getItem();

  constructor() {
    effect(() => {
      if (this.item() && this.state() == clpp.Player.State.PLAYING) {
        this.loadData();
        this.buildMenus();
      } else if (!this.item()) {
        this.menus.set([]);
      }
    })
  }

  private loadData() {
    this.audioTracks.set(this.playerService.getAudioTracks());
    this.selectedAudioTrack.set(this.playerService.getCurrentAudioTrack());
    this.subtitleTracks.set(this.playerService.getSubtitleTracks());
    this.selectedSubtitleTrack.set(this.playerService.getCurrentSubtitleTrack());
    this.selectedSpeed.set(this.playerService.getSpeed());
  }

  private buildMenus() {
    const audioTracks = this.audioTracks().filter(track => track && track.language !== null);
    const subtitleTracks = this.subtitleTracks().filter(track => track && track.language !== null);
    const minSpeed = this.minSpeed();
    const maxSpeed = this.maxSpeed();
    const menus: Array<any> = [];

    if (audioTracks.length > 0) {
      menus.push({
        id: 'audio',
        name: translate('player.languages.audio'),
        items: audioTracks.map(track => ({
          id: track.id,
          track: track,
          displayText: this.getTrackDisplayText(track, audioTracks)
        }))
      });
    }

    // Always show subtitles menu if there are tracks, and include an "Off" option
    if (subtitleTracks.length > 0) {
      const items = subtitleTracks.map(track => ({
        id: track.id,
        track: track,
        displayText: this.getTrackDisplayText(track, subtitleTracks)
      }));

      // Add "Off" option at the beginning
      items.unshift({
        id: 'off',
        track: null,
        displayText: this.getTrackDisplayText()
      });

      menus.push({
        id: 'subtitles',
        name: translate('player.languages.subtitles'),
        items: items
      });
    }

    menus.push({
      id: 'speed',
      name: translate('player.speed'),
      items: this.availableSpeeds.map(id => ({
        id: id,
        displayText: id,
        disabled: id < minSpeed || id > maxSpeed
      }))
    });

    // A rate the range no longer allows drops back to normal speed: a sped-up DVR replay that
    // catches up with the live edge, or a rate carried over from a previous VOD item.
    const speed = this.playerService.getSpeed();
    if (speed < minSpeed || speed > maxSpeed) {
      this.playerService.setSpeed(1);
      this.selectedSpeed.set(1);
    }

    this.menus.set(menus);
  }

  public select(menu: any, item: any) {
    if (item.disabled) return;
    if (menu.id == 'subtitles') {
      this.selectSubtitle(item);
    }
    else if (menu.id == 'audio') {
      this.selectAudio(item);
    }
    else if (menu.id == 'speed') {
      this.selectSpeed(item.id);
    }
    this.loadData();
  }

  private selectAudio(item: any) {
    this.playerService.setAudioTrack(item.track);
  }

  private selectSubtitle(item: any) {
    this.playerService.setSubtitleTrack(item.track);
  }

  private selectSpeed(value: number) {
    this.playerService.setSpeed(value);
  }

  private getTrackDisplayText(track?: any, allTracks?: any[]): string {
    if (!track) {
      return translate('common.label.off');
    }

    const lang = track.language;
    const label = track.label;

    // AC1 & Scenario 3: Language "und" (Undefined) or missing
    if (lang === 'und' || !lang) {
      if (label) return label;
      return translate('player.languages.unknown');
    }

    // AC2: Dynamic localization lookup
    const localizationKey = `player.languages.${lang}`;
    let localized = translate(localizationKey);

    // AC3: Fallback logic (if key not found or empty)
    if (!localized || localized === localizationKey) {
      if (label) {
        localized = label;
      } else {
        localized = lang.toUpperCase();
      }
    }

    // Scenario 1: Duplicate languages with different details
    const duplicates = allTracks.filter(t => t && t.language === lang);
    if (duplicates.length > 1 && label) {
      return `${localized} (${label})`;
    }

    return localized;
  }

  public isSelected(menuId: string, itemId: string | number): boolean {
    switch (menuId) {
      case 'subtitles':
        const selectedSubtitle = this.selectedSubtitleTrack();
        if (itemId === 'off') return selectedSubtitle === null;
        return selectedSubtitle && itemId === selectedSubtitle.id;
      case 'audio':
        const selectedAudio = this.selectedAudioTrack();
        return selectedAudio && itemId === selectedAudio.id;
      default:
        return itemId === this.selectedSpeed();
    }
  }

}